import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function ReceiptImport({ pantryItems, onImport, onClose, apiFetch }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('upload'); // upload | loading | review
  const [error, setError] = useState(null);
  const [parsedItems, setParsedItems] = useState([]);
  const [decisions, setDecisions] = useState({});
  // decisions[index] = { action: 'match'|'assign'|'create'|'ignore', pantryName?: string, newName?: string, section?: string, qtyOverride?: number }
  const fileInputRef = useRef(null);

  const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const ACCEPT_STRING = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif';

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setError(t('receipt.unsupportedFormat'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError(t('receipt.tooLarge'));
      return;
    }

    setError(null);
    setStep('loading');

    try {
      const base64 = await fileToBase64(file);
      const bodyField = isPdf ? 'pdf' : 'image';
      const res = await apiFetch('/api/receipt/parse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [bodyField]: base64 }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to parse receipt');
      }

      const { items } = await res.json();
      setParsedItems(items);

      // Pre-populate decisions
      const initial = {};
      items.forEach((item, i) => {
        if (item.match && item.match.confidence >= 0.6) {
          initial[i] = { action: 'match', pantryName: item.match.name };
        } else if (item.match) {
          initial[i] = { action: 'assign', pantryName: item.match.name };
        } else {
          initial[i] = { action: 'ignore' };
        }
      });
      setDecisions(initial);
      setStep('review');
    } catch (err) {
      setError(err.message);
      setStep('upload');
    }
  };

  const handleDecisionChange = (index, action) => {
    setDecisions(prev => ({
      ...prev,
      [index]: { ...prev[index], action },
    }));
  };

  const handleAssignTarget = (index, pantryName) => {
    setDecisions(prev => ({
      ...prev,
      [index]: { ...prev[index], action: 'assign', pantryName },
    }));
  };

  const handleNewName = (index, newName) => {
    setDecisions(prev => ({
      ...prev,
      [index]: { ...prev[index], newName },
    }));
  };

  const handleNewSection = (index, section) => {
    setDecisions(prev => ({
      ...prev,
      [index]: { ...prev[index], section },
    }));
  };

  const handleQtyOverride = (index, value) => {
    const num = value === '' ? undefined : parseFloat(value);
    setDecisions(prev => ({
      ...prev,
      [index]: { ...prev[index], qtyOverride: num },
    }));
  };

  const handleConfirmImport = () => {
    const imports = [];
    parsedItems.forEach((item, i) => {
      const d = decisions[i];
      if (!d) return;
      const defaultQty = item.suggestedQty != null ? item.suggestedQty : item.quantity;
      const qty = d.qtyOverride != null && Number.isFinite(d.qtyOverride) ? d.qtyOverride : defaultQty;
      if (d.action === 'match' || d.action === 'assign') {
        imports.push({
          type: 'update',
          pantryName: d.pantryName,
          addQty: qty,
        });
      } else if (d.action === 'create') {
        imports.push({
          type: 'create',
          name: d.newName || item.simpleName,
          qty,
          section: d.section || item.section,
        });
      }
      // 'ignore' → skip
    });
    if (imports.length > 0) {
      onImport(imports);
    }
    onClose();
  };

  const importCount = Object.values(decisions).filter(d => d.action !== 'ignore').length;

  if (step === 'upload') {
    return (
      <div className="receipt-overlay">
        <div className="receipt-modal">
          <div className="receipt-modal-header">
            <h2>{t('receipt.importTitle')}</h2>
            <button type="button" className="receipt-close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="receipt-upload-area" onClick={() => fileInputRef.current?.click()}>
            <div className="receipt-upload-icon">📄</div>
            <p className="receipt-upload-text">{t('receipt.selectFile')}</p>
            <p className="receipt-upload-hint">{t('receipt.supportedFormats')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_STRING}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>
          {error && <div className="receipt-error">{error}</div>}
        </div>
      </div>
    );
  }

  if (step === 'loading') {
    return (
      <div className="receipt-overlay">
        <div className="receipt-modal">
          <div className="receipt-modal-header">
            <h2>{t('receipt.importTitle')}</h2>
            <button type="button" className="receipt-close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="receipt-loading">
            <div className="receipt-spinner"></div>
            <p>{t('receipt.reading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // step === 'review'
  const matchedItems = parsedItems
    .map((item, i) => ({ ...item, _index: i, decision: decisions[i] }))
    .filter(item => item.decision?.action === 'match');

  const unmatchedItems = parsedItems
    .map((item, i) => ({ ...item, _index: i, decision: decisions[i] }))
    .filter(item => item.decision?.action !== 'match');

  return (
    <div className="receipt-overlay">
      <div className="receipt-modal receipt-modal--review">
        <div className="receipt-modal-header">
          <h2>{t('receipt.reviewItems', { count: parsedItems.length })}</h2>
          <button type="button" className="receipt-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="receipt-review-scroll">
          {matchedItems.length > 0 && (
            <div className="receipt-section">
              <div className="receipt-section-header receipt-section-header--matched">
                {t('receipt.matched', { count: matchedItems.length })}
              </div>
              {matchedItems.map(item => {
                const defaultQty = item.suggestedQty != null ? item.suggestedQty : item.quantity;
                const unit = item.unitWeight ? item.unitWeight.unit : '';
                return (
                  <div key={item._index} className="receipt-item receipt-item--matched">
                    <div className="receipt-item-info">
                      <span className="receipt-item-name">{item.rawName}</span>
                      <span className="receipt-item-detail">
                        +<input
                          type="number"
                          className="receipt-qty-input"
                          value={item.decision.qtyOverride != null ? item.decision.qtyOverride : defaultQty}
                          onChange={(e) => handleQtyOverride(item._index, e.target.value)}
                          min="0"
                          step="any"
                        />{unit}
                        {' → '}<strong>{item.decision.pantryName}</strong>
                      </span>
                    </div>
                    <button
                      type="button"
                      className="receipt-item-action-btn receipt-item-action-btn--remove"
                      onClick={() => handleDecisionChange(item._index, 'ignore')}
                      title={t('receipt.ignoreTitle')}
                    >✕</button>
                  </div>
                );
              })}
            </div>
          )}

          {unmatchedItems.length > 0 && (
            <div className="receipt-section">
              <div className="receipt-section-header receipt-section-header--unmatched">
                {t('receipt.needsReview', { count: unmatchedItems.length })}
              </div>
              {unmatchedItems.map(item => (
                <ReceiptUnmatchedItem
                  key={item._index}
                  item={item}
                  pantryItems={pantryItems}
                  suggestedMatch={item.match}
                  onAction={(action) => handleDecisionChange(item._index, action)}
                  onAssignTarget={(name) => handleAssignTarget(item._index, name)}
                  onNewName={(name) => handleNewName(item._index, name)}
                  onNewSection={(section) => handleNewSection(item._index, section)}
                  onQtyOverride={(value) => handleQtyOverride(item._index, value)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="receipt-modal-footer">
          <button
            type="button"
            className="receipt-cancel-btn"
            onClick={onClose}
          >
            {t('receipt.cancel')}
          </button>
          <button
            type="button"
            className="receipt-confirm-btn"
            onClick={handleConfirmImport}
            disabled={importCount === 0}
          >
            {t('receipt.addToPantry', { count: importCount })}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptUnmatchedItem({ item, pantryItems, suggestedMatch, onAction, onAssignTarget, onNewName, onNewSection, onQtyOverride }) {
  const { t } = useTranslation();
  const decision = item.decision || {};
  const isAssigning = decision.action === 'assign';
  const isCreating = decision.action === 'create';
  const isIgnored = decision.action === 'ignore';
  const defaultQty = item.suggestedQty != null ? item.suggestedQty : item.quantity;
  const unit = item.unitWeight ? item.unitWeight.unit : '';

  return (
    <div className={`receipt-item receipt-item--unmatched${isIgnored ? ' receipt-item--ignored' : ''}`}>
      <div className="receipt-item-info">
        <span className="receipt-item-name">{item.rawName}</span>
        <span className="receipt-item-qty">
          +<input
            type="number"
            className="receipt-qty-input"
            value={decision.qtyOverride != null ? decision.qtyOverride : defaultQty}
            onChange={(e) => onQtyOverride(e.target.value)}
            min="0"
            step="any"
          />{unit}
        </span>
      </div>
      <div className="receipt-item-actions">
        <button
          type="button"
          className={`receipt-action-chip${isAssigning ? ' active' : ''}`}
          onClick={() => {
            if (suggestedMatch) {
              onAssignTarget(suggestedMatch.name);
            }
            onAction('assign');
          }}
        >
          {t('receipt.assignToExisting')}
        </button>
        <button
          type="button"
          className={`receipt-action-chip${isCreating ? ' active' : ''}`}
          onClick={() => {
            onNewName(item.simpleName);
            onNewSection(item.section);
            onAction('create');
          }}
        >
          {t('receipt.createNew')}
        </button>
        <button
          type="button"
          className={`receipt-action-chip${isIgnored ? ' active' : ''}`}
          onClick={() => onAction('ignore')}
        >
          {t('receipt.ignore')}
        </button>
      </div>

      {isAssigning && (
        <div className="receipt-assign-picker">
          <select
            className="receipt-select"
            value={decision.pantryName || ''}
            onChange={(e) => onAssignTarget(e.target.value)}
          >
            <option value="">{t('receipt.selectPantryItem')}</option>
            {pantryItems
              .filter(p => p.name)
              .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
              .map(p => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
          </select>
        </div>
      )}

      {isCreating && (
        <div className="receipt-create-fields">
          <input
            className="receipt-create-input"
            type="text"
            placeholder={t('receipt.itemName')}
            value={decision.newName || ''}
            onChange={(e) => onNewName(e.target.value)}
          />
          <select
            className="receipt-select receipt-select--section"
            value={decision.section || item.section}
            onChange={(e) => onNewSection(e.target.value)}
          >
            <option value="fresh">{t('receipt.freshOption')}</option>
            <option value="cupboard">{t('receipt.cupboardOption')}</option>
          </select>
        </div>
      )}
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default ReceiptImport;
